import React, { useEffect } from 'react';
import { useState } from 'react';
import { useAsync } from 'react-async';
import { Routes, Route, useParams } from 'react-router-dom';
import ExperienceForm from './Partials/ExperienceForm';
import { useApp, useSearchParams, useSnack } from 'hooks';
import { useTranslation } from 'react-i18next';
import { formatExperienceApiData } from './Utilities/ExperienceHelpers';
import PromotionsContainer from '../Promotions';
import { decodeId, initialLocale } from '../../../utils';
import { ExperienceCompatibility } from '../Compatibility/Compatibility';
import { ExtraRecommendation } from '../Recommandation/ExtraRecommendation';
import ExperienceHistoryList from '../History/ExperienceHistoryList';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import {
  getExperience,
  getExperienceCancelConditions,
  saveExperienceCancelConditions,
  updateExperience,
} from 'api/node/experiences';
import { omit } from 'lodash';
import {
  formatCancellationPoliciesToForm,
  formatCancellationPoliciesToPayload,
} from 'components/Fields/CancelConditionField/utils';

export default function ExperienceEdit() {
  const { t } = useTranslation('experiences');
  const { experienceId } = useParams();
  const [searchParams] = useSearchParams();
  const { notify, notifyError } = useSnack();
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const urlLocale = new URLSearchParams(window.location.search).get('locale');
  const [currentLocale, setCurrentLocale] = useState(
    urlLocale ? { value: urlLocale } : initialLocale,
  );
  const { currentOrganizationId } = useApp();

  const {
    data: experienceData,
    isLoading,
    reload,
  } = useAsync({
    promiseFn: getExperience,
    experienceId: experienceId,
    locale: currentLocale?.value,
  });

  const { data: cancelConditionsData } = useAsync({
    promiseFn: getExperienceCancelConditions,
    experienceId: experienceId,
  });

  const cancelConditions = formatCancellationPoliciesToForm(
    cancelConditionsData?.data ?? [],
  );

  useEffect(() => {
    if (experienceData) {
      reload();
    }
  }, [currentLocale?.value]);

  const experienceBdd = experienceData ? experienceData?.data : [];

  const experienceInitialValues = formatExperienceApiData(experienceBdd);

  const programPictureIds =
    experienceData?.data?.programs?.map((p) => p.picture?.id).filter(Boolean) ??
    [];

  const isReadOnly =
    Boolean(experienceBdd?.isTransitionalProgram) &&
    Boolean(currentOrganizationId) &&
    experienceBdd?.organizationId?.toString() !==
      decodeId(currentOrganizationId)?.toString();

  const handleSave = async (experience) => {
    const allPictures = experience.pictures || [];

    const picturesInPrograms = allPictures.filter((picture) =>
      programPictureIds.includes(picture.id),
    );

    const mediaPictures = allPictures.filter(
      (picture) => !programPictureIds.includes(picture.id),
    );

    try {
      setSaveIsLoading(true);
      const payload = {
        ...omit(experience, 'cancelConditions'),
        pictures: mediaPictures,
        programs: experience?.programs?.map((program) => ({
          ...program,
          picture: program?.picture?.id
            ? picturesInPrograms.find(
                (picture) => picture.id === program.picture.id,
              ) || null
            : program?.picture,
        })),
      };

      await updateExperience({
        experienceId,
        payload,
        locale: currentLocale?.value,
      });
      await saveExperienceCancelConditions({
        experienceId,
        payload: formatCancellationPoliciesToPayload(
          experience.cancelConditions,
        ),
      });
      notify(t('editSuccess'));
      setSaveIsLoading(false);
      reload();
      return experienceId;
    } catch (err) {
      setSaveIsLoading(false);
      if (err?.response?.data?.message) {
        notify(err?.response?.data?.message, { type: 'error' });
      } else {
        notifyError(err);
      }
    }
  };

  return (
    <Routes>
      <Route
        path=""
        element={
          <>
            {saveIsLoading && (
              <KoobLoadingOverlay
                opacity="semi"
                title={t('saving.title')}
                description={t('saving.description')}
              />
            )}

            {isLoading && (
              <KoobLoadingOverlay
                opacity="semi"
                title={t('getting.title')}
                description={t('getting.description')}
              />
            )}

            {(!isLoading || experienceInitialValues) && (
              <ExperienceForm
                initialValues={{
                  ...experienceInitialValues,
                  cancelConditions,
                }}
                handleSave={handleSave}
                initialStep={
                  searchParams.step && searchParams.step !== ''
                    ? parseInt(searchParams.step)
                    : 1
                }
                currentLocale={currentLocale}
                setCurrentLocale={setCurrentLocale}
                isReadOnly={isReadOnly}
              />
            )}
          </>
        }
      />
      <Route
        path="promotions/*"
        element={<PromotionsContainer isReadOnly={isReadOnly} />}
      />
      <Route
        path="compatibility/*"
        element={<ExperienceCompatibility reloadExperience={reload} />}
      />
      <Route path="recommendation/*" element={<ExtraRecommendation />} />
      <Route path="history" element={<ExperienceHistoryList />} />
    </Routes>
  );
}
